import * as React from "react";
import {
  Alert,
  Backdrop,
  CircularProgress,
  Grid,
  Snackbar,
  Stack,
} from "@mui/material";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import AccountInformation from "./AccountInformation";
import OrganizationInformation from "../organizations/OrganizationInformation";
import ChangePassword from "./ChangePassword";
import { useFlash } from "../FlashContext";

interface Props {
  user_id: string;
  selectedCustomer: string;
  customers: string;
  setRefreshUser: any;
}

export interface User {
  id?: string | number;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
  customer: string | number;
}

const ProviderEdit: React.FC<Props> = (props: any) => {
  const [unsavedChanges, setUnsavedChanges] = React.useState<boolean>(false);
  const [accountExpanded, setAccountExpanded] = React.useState(false);
  const [changePasswordExpanded, setChangePasswordExpanded] =
    React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [customerSelection, setCustomerSelection] = React.useState<any>();
  const [organization, setOrganization] = React.useState(null);

  //  context import
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [unchangedUser, setUnchangedUser] = React.useState(null);

  const [user, setUser] = React.useState<User>({
    id: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    mobilePhone: "",
    customer: props.selectedCustomer?.customer_id,
  });

  const handleChange = <K extends keyof User>(field: K, value: User[K]) => {
    setUser({ ...user, [field]: value });
    setUnsavedChanges(true);
  };

  React.useEffect(() => {
    if (props.user_id) {
      fetch(`/data_fetching/edit_provider`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            console.log(result.error);
          } else {
            const userInfo = result.resource.user;
            setUser({
              id: userInfo.id,
              firstName: userInfo.first_name,
              middleName: userInfo.middle_name,
              lastName: userInfo.last_name,
              mobilePhone: userInfo.mobile_phone_number,
              email: userInfo.email,
              customer: props.selectedCustomer?.customer_id,
            });
            setUnchangedUser({
              id: userInfo.id,
              firstName: userInfo.first_name,
              middleName: userInfo.middle_name,
              lastName: userInfo.last_name,
              email: userInfo.email,
              mobilePhone: userInfo.mobile_phone_number,
              customer: props.selectedCustomer?.customer_id,
            });
            setCustomerSelection(userInfo.customer_selection_id);
            setOrganization(result.resource.organization);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [props.user_id]);

  const validForm = () => {
    let valid = false;

    const fields = [
      [user.firstName, "First Name"],
      [user.lastName, "Last Name"],
      [user.email, "Email Address"],
      [user.mobilePhone, "Mobile Phone"],
    ];
    const invalid = fields.filter((field) => !field[0] || field[0].length == 0);
    let invalidFieldArrayObject = invalid.map((field) => field[1]);

    if (invalidFieldArrayObject.length == 0) {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      if (!re.test(String(user.email).toLowerCase())) {
        let invalidFields = invalidFieldArrayObject;
        invalidFields.push("a valid Email Address");
        valid = false;
      } else {
        valid = true;
      }
    }

    return valid;
  };

  const handleUpdateUser = () => {
    if (validForm()) {
      setLoading(true);
      fetch(`/customer_selections/${customerSelection}`, {
        method: "PATCH",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          new_customer_id: user.customer,
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (typeof result.error !== "undefined") {
            showToast({
              title: "Failed to Update",
              text: result.error,
              type: "error",
            });
            setLoading(false);
          } else {
            updateUserInfo();
          }
        })
        .catch((error) => {
          showToast({
            title: "Error",
            text: error,
            type: "error",
          });
          setLoading(false);
        });
    } else {
      showToast({
        title: "Invalid Field",
        text: "Please fill in all required fields correctly.",
        type: "error",
      });
      setLoading(false);
    }
  };

  const updateUserInfo = () => {
    fetch(`/edit_provider`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        user: {
          first_name: user.firstName,
          last_name: user.lastName,
          middle_name: user.middleName,
          mobile_phone_number: user.mobilePhone,
          email: user.email,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        const userInfo = result.resource;

        if (result.success) {
          setUser({
            id: userInfo.id,
            firstName: userInfo.first_name,
            middleName: userInfo.middle_name,
            lastName: userInfo.last_name,
            email: userInfo.email,
            mobilePhone: userInfo.mobile_phone_number,
            customer: userInfo.default_customer,
          });
          setUnchangedUser({
            id: userInfo.id,
            firstName: userInfo.first_name,
            middleName: userInfo.middle_name,
            lastName: userInfo.last_name,
            email: userInfo.email,
            mobilePhone: userInfo.mobile_phone_number,
            customer: userInfo.default_customer,
          });

          showToast({
            title: "Account Information Updated",
            text: "Account Information has been updated",
            type: "success",
          });
          setLoading(false);
          setAccountExpanded(false);
          props.setRefreshUser(true);
        } else {
          showToast({
            title: "Error",
            text: result.error,
            type: "error",
          });
          setLoading(false);
        }
      })
      .catch((error) => {
        showToast({
          title: "Error",
          text: error,
          type: "error",
        });
        setLoading(false);
      });
  };

  const resetUserInfo = () => {
    if (unchangedUser) {
      setUser({
        id: unchangedUser.id,
        firstName: unchangedUser.firstName,
        middleName: unchangedUser.middleName,
        lastName: unchangedUser.lastName,
        mobilePhone: unchangedUser.mobilePhone,
        email: unchangedUser.email,
        customer: unchangedUser.customer,
      });
    }
  };

  const fetchCustomerInformation = () => {
    if (props.customerId) {
      fetch("/data_fetching/customers/current_organization_information", {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (typeof result.error !== "undefined") {
            console.log(result.error);
          } else {
            setOrganization(result.customer);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  React.useEffect(() => {
    fetchCustomerInformation();
  }, []);

  return (
    <Grid container className="profile-panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  paddingX={3}
                  paddingY={1}
                  alignItems={"center"}
                  display={"flex"}
                >
                  <Grid item>
                    <p className="panel-header">Profile Overview</p>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>

            <Grid container className="main-content-container">
              <Grid
                item
                xs={12}
                display="flex"
                flexDirection="column"
                rowGap={2.5}
              >
                <AccountInformation
                  user={user}
                  unsavedChanges={unsavedChanges}
                  setUnsavedChanges={setUnsavedChanges}
                  customers={props.customers}
                  handleUpdateUser={handleUpdateUser}
                  handleChange={handleChange}
                  resetUserInfo={resetUserInfo}
                  expanded={accountExpanded}
                  setExpanded={setAccountExpanded}
                />

                <OrganizationInformation organization={organization} />
                <ChangePassword
                  expanded={changePasswordExpanded}
                  setExpanded={setChangePasswordExpanded}
                  csrfToken={authenticationSetting.csrfToken}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </Grid>
  );
};

export default ProviderEdit;
